import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { habitStatsViewing } from '@goaltree/backend/convex/lib/viewingContext'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { BarChart3Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { HabitStatsSkeleton, HabitStatsView } from '@/components/habit-stats-view'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { ProGate } from '@/components/pro-gate'
import { SiteHeader } from '@/components/site-header'
import { useIsPro } from '@/hooks/use-is-pro'
import { useScrollTitle } from '@/hooks/use-scroll-title'
import { useReportViewing } from '@/lib/coach-call-store'

export const Route = createFileRoute('/_authenticated/habits/$habitId')({
  head: () => ({
    meta: [{ title: 'Habit stats — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { habitId } = Route.useParams()
  const router = useRouter()
  const { isPro, isLoading } = useIsPro()
  const stats = useQuery(
    api.habitStats.getHabitStats,
    isPro ? { habitId: habitId as Id<'nodes'> } : 'skip',
  )
  useReportViewing(() => (stats ? habitStatsViewing(stats) : null))
  const { onScroll } = useScrollTitle(t('habitStats.eyebrow'))
  const back = () =>
    window.history.length > 1 ? router.history.back() : router.navigate({ to: '/today' })

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader
          title={t('habitStats.eyebrow')}
          showInbox={false}
          showSearch={false}
          onBack={back}
        />
        <SiteHeader className="hidden lg:flex" />
        {isLoading ? null : !isPro ? (
          <ProGate preview={<HabitStatsSkeleton />} />
        ) : (
          <div
            className="flex-1 overflow-y-auto pb-36 lg:pb-24"
            onScroll={onScroll}
            data-scroll-reset
          >
            <div className="px-4 pt-4 pb-4">
              <div className="mx-auto max-w-(--page-col)">
                {stats === undefined ? (
                  <HabitStatsSkeleton />
                ) : stats === null ? (
                  <div className="flex flex-col items-center gap-3 py-24 text-center">
                    <BarChart3Icon className="h-12 w-12 text-muted-foreground/40" />
                    <p className="text-xl font-bold text-foreground">{t('habitStats.notFound')}</p>
                    <p className="text-sm text-muted-foreground">{t('habitStats.notFoundDesc')}</p>
                  </div>
                ) : (
                  <HabitStatsView stats={stats} />
                )}
              </div>
            </div>
          </div>
        )}
      </SidebarInset>
    </SidebarProvider>
  )
}
